/**
 * Every failure the console shows a person is written in plain language here.
 *
 * Raw daemon and platform errors name commands, protocols and internal contracts
 * ("set_fixture_params: must target at least one fixture, group, or element set"). Those are for the
 * console log and for staff, never for the room's operator — so the UI renders ONLY the strings this
 * module produces, and the raw text travels alongside as `detail` for `console.error` and the
 * staff-only disclosure (`TechnicalDetails` in `components/ui.tsx`).
 *
 * `plainErrors.test.ts` fails if a mapped message leaks a command name; `operatorCopy.test.ts` fails if
 * one of these `message` strings names anything internal at all.
 */

import { EFFECT_MAP_NOT_LOADED, EFFECT_MAP_RIG_MISMATCH } from './effectLayout';
import { effectUnavailableMessage, ROOM_UPDATED_NOTICE } from './operatorCopy';

export interface PlainError {
  /** The sentence a person reads. */
  message: string;
  /** The raw text, for console.error and the staff-only disclosure. Never rendered to an operator. */
  detail: string;
}

/**
 * One sentence for "we looked that email up and nobody is there", wherever it is asked.
 *
 * Firebase answers it as `auth/user-not-found`; the platform answers it as a refusal from
 * `resolveMemberTarget`. It is the same fact to the person reading it, so it is one string.
 */
export const NO_ACCOUNT_MESSAGE = 'That email does not have an account.';

/**
 * THE RULE FOR EDITING THIS TABLE: a pattern keys on error VOCABULARY — a code, a protocol token, a
 * status word — never on ordinary English that a human-facing server message may legitimately contain.
 *
 * On 2026-09-15 the credential rule carried a bare `sign ?-?in` alternative. The platform refused an Add
 * with the advice "No account for that email — ask them to sign in to Nuance once, then re-add.", the
 * alternative matched the advice, and a venue owner whose session was perfectly good was told their
 * browser needed to sign in again. Every bare-prose alternative is that bug waiting for its sentence:
 * `role` matched "Invalid params for setMemberRole.", `stream` matched "stream: handshake payload too
 * short", `missing` matched "auth/missing-password". They are bounded below, and new ones must be too.
 */
const PATTERNS: Array<{ test: RegExp; message: string }> = [
  {
    test: /permission[- ]denied|not authorized|unauthorized|insufficient (permissions?|role)|requires? [a-z]+ (role|permission)|requires (manager|owner|staff)|member cannot/i,
    message: 'You do not have permission to do that.',
  },
  {
    test: /cannot test audio input while audio effect .* is running/i,
    message: 'An audio effect is using the input. Stop the effect first, then test the input.',
  },
  {
    test: /audio input monitor is already running/i,
    message: 'The input test is already running.',
  },
  {
    // Keep this ahead of the generic timeout rule: connection failures often contain "deadline".
    test: /(connect|start) audio monitor sidecar/i,
    message: 'The controller could not open that audio input. Check the cable and that nothing else is using the interface.',
  },
  {
    test: /audio monitor input mismatch/i,
    message: 'The controller opened a different input than requested.',
  },
  {
    test: /device (is )?(offline|unreachable|not connected)|controller (is )?offline|no (active )?(session|heartbeat)|failed-precondition: device/i,
    message: 'The controller is offline.',
  },
  {
    test: /deadline|timed? ?out|timeout/i,
    message: 'The controller did not answer in time. Try again.',
  },
  {
    test: /network|fetch failed|unavailable|ERR_INTERNET|offline|ENOTFOUND|ECONNREFUSED|failed to connect/i,
    message: 'The connection dropped. Reconnecting…',
  },
  {
    // Ahead of the credential rule: the controller's own console has no sign-in to send anyone back to.
    test: /setup ?code|pair(ing)? (failed|rejected|denied)|invalid (setup )?(code|pin)/i,
    message: 'That setup code was not accepted.',
  },
  {
    // Ahead of the credential rule, whose sentence this refusal used to borrow. Reachable from any path
    // that names a person by email — the console's own Add invites instead, but the staff CLI and the
    // owner-by-email arguments still resolve a target this way.
    test: /no account for that email/i,
    message: NO_ACCOUNT_MESSAGE,
  },
  {
    test: /revoked|token (is )?expired|credential|unauthenticated/i,
    message: 'This browser needs to sign in again.',
  },
  {
    test: /must target at least one|no fixtures resolved|empty (device id|target)|elementValues/i,
    message: 'Nothing was selected, so there was nothing to change.',
  },
  {
    // The console reloads the room's setup and the operator presses Start again; that is the whole story.
    // The same pattern the card's reload keys on (`effectLayout.ts`), so the two cannot drift apart.
    test: EFFECT_MAP_RIG_MISMATCH,
    message: ROOM_UPDATED_NOTICE,
  },
  {
    // Audio reactive's Start on a controller whose loaded rig carries no room geometry. Over the cloud the
    // controller's refusal is the check (S38); to the operator it is the same fact as Edges' refusals.
    test: EFFECT_MAP_NOT_LOADED,
    message: effectUnavailableMessage('This effect'),
  },
  {
    // Pi 1.7.29 refuses name-only drafts during authoring; the current catalog and Start share this sentence.
    test: /auto vj: set has no units to play/i,
    message: 'Auto VJ set has nothing to play',
  },
  {
    // Both Edges refusals — a room the controller does not recognise, and geometry that does not match
    // what it has loaded — are one outcome to the person in the room. Which one it was is in `detail`.
    test: /Edges layout (roomId.*does not match loaded room|digest.*does not match loaded rig schematic)/i,
    message: effectUnavailableMessage('This effect'),
  },
  {
    // `stream` alone matched the controller's own wire complaints ("stream: handshake payload too
    // short"), which have nothing to do with Nuance holding the room.
    test: /remote[- _]?live|active source|\b(streaming|stream) (is )?active\b|\bactive stream\b/i,
    message: 'Streaming from Nuance is active. Disconnect the stream before changing the room.',
  },
  {
    test: /another (device|operator|transport)|controlled-elsewhere/i,
    message: 'The controller connection changed. Try again.',
  },
  {
    test: /rate ?limit|too many requests|resource[- ]exhausted/i,
    message: 'That was too many changes at once. Try again in a moment.',
  },
  {
    // `missing` as a word, never as the head of a Firebase code: `auth/missing-password` is a form the
    // person has not filled in, not a thing that has gone away.
    test: /not found|no such|\bmissing\b(?!-)/i,
    message: 'That is no longer there. Reload and try again.',
  },
];

/** The room-action catch-all: an operator never sees a protocol string we did not anticipate. */
export const GENERIC_ROOM_FAILURE = "Couldn't apply that change to the room. Try again.";
export const GENERIC_SIGN_IN_FAILURE = "Couldn't sign in. Check your email and password and try again.";
/** The controller's own console has no account to fall back to: pairing owns its whole failure copy. */
export const PAIRING_FAILURE = 'That setup code was not accepted.';
export const SIGN_IN_SERVICE_FAILURE = "Couldn't reach Nuance Venue. Try again in a moment.";

const SIGN_IN_SERVICE_PATTERN = /auth\/(?:internal-error|network-request-failed)|internal[- ]error|network|fetch failed|service unavailable|server error|\b5\d\d\b|ERR_INTERNET|ENOTFOUND|ECONNREFUSED/i;

export function rawErrorText(error: unknown): string {
  if (error == null) return '';
  if (typeof error === 'string') return error;
  const candidate = error as { message?: unknown; code?: unknown; details?: unknown };
  const parts = [candidate.code, candidate.message, candidate.details]
    .filter((part) => typeof part === 'string' && part.length > 0);
  if (parts.length) return parts.join(': ');
  if (error instanceof Error) return error.name;
  return String(error);
}

/**
 * Map anything thrown by the relay, the daemon, Firebase or our own code onto one plain sentence.
 * `fallback` lets a caller phrase the generic case for its own surface (e.g. "The room drawing could
 * not load."), and is itself required to be plain language.
 */
export function plainError(error: unknown, fallback = GENERIC_ROOM_FAILURE): PlainError {
  const detail = rawErrorText(error);
  const matched = PATTERNS.find((pattern) => pattern.test.test(detail));
  return { message: matched ? matched.message : fallback, detail };
}

/** Auth owns its fallbacks so a backend failure can never inherit room-command copy. */
export function plainSignInError(error: unknown): PlainError {
  const detail = rawErrorText(error);
  if (SIGN_IN_SERVICE_PATTERN.test(detail)) return { message: SIGN_IN_SERVICE_FAILURE, detail };
  return plainError(error, GENERIC_SIGN_IN_FAILURE);
}

export const ACCOUNT_CREATE_FAILURE = "Couldn't create the account. Try again.";
export const PASSWORD_RESET_FAILURE = "Couldn't send the reset email. Try again.";
export const VERIFICATION_SEND_FAILURE = "Couldn't send the verification email. Try again.";

/**
 * The Firebase Auth refusals a person can actually act on, matched by code before anything else.
 *
 * These have to come first: the generic table above reads `auth/user-not-found` as "that is no longer
 * there" and `auth/invalid-credential` as "this browser needs to sign in again", neither of which is
 * true of someone typing their address into a reset form.
 */
const ACCOUNT_PATTERNS: Array<{ test: RegExp; message: string }> = [
  { test: /auth\/email-already-in-use/i, message: 'That email already has an account. Sign in instead.' },
  { test: /auth\/invalid-email/i, message: 'That email address is not valid.' },
  { test: /auth\/weak-password|password should be at least/i, message: 'Use at least 6 characters.' },
  { test: /auth\/user-not-found/i, message: NO_ACCOUNT_MESSAGE },
  { test: /auth\/too-many-requests/i, message: 'Too many tries. Try again in a few minutes.' },
  { test: /auth\/operation-not-allowed/i, message: 'This console does not accept email and password accounts.' },
];

/**
 * Map an account-lifecycle failure (create account, reset password, resend verification) onto one plain
 * sentence. `fallback` is the surface's own generic case, so a reset never inherits create-account copy.
 */
export function plainAccountError(error: unknown, fallback = ACCOUNT_CREATE_FAILURE): PlainError {
  const detail = rawErrorText(error);
  const matched = ACCOUNT_PATTERNS.find((pattern) => pattern.test.test(detail));
  if (matched) return { message: matched.message, detail };
  if (SIGN_IN_SERVICE_PATTERN.test(detail)) return { message: SIGN_IN_SERVICE_FAILURE, detail };
  return { message: fallback, detail };
}

/**
 * Report a failure: the plain sentence goes back to the caller for rendering, the raw text goes to the
 * console for whoever is debugging. One funnel, so nothing can render a raw string by accident.
 */
export function reportError(context: string, error: unknown, fallback = GENERIC_ROOM_FAILURE): PlainError {
  const plain = plainError(error, fallback);
  if (plain.detail) console.error(`[nuance-venue] ${context}: ${plain.detail}`, error);
  return plain;
}
